import { httpClient, stringifyOpts } from 'providers/clients/httpClient';
import { fetchUtils } from 'react-admin';

// https://platform.openai.com/docs/guides/latest-model#meet-the-models
export type OpenAiModel =
  | 'gpt-3.5-turbo'
  | 'gpt-4'
  | 'gpt-4-turbo'
  | 'gpt-4o'
  | 'gpt-4.1-mini'
  | 'gpt-5'
  | 'gpt-5-mini'
  | 'gpt-5-nano'
  | 'gpt-5-chat-latest'
  | 'gpt-5.1';

export const DEFAULT_AI_MODEL: OpenAiModel = 'gpt-4.1-mini';

export type ChatRequestMessage = {
  role: string; // eg: 'system' | 'user'
  content: string;
};

// request body
export type ChatApiRequest = {
  model: OpenAiModel;
  messages: ChatRequestMessage[];
};

export type ChatResponseMessage = {
  role: string;
  content: string;
  refusal: boolean | null;
  annotations: string[];
};

export type ChatResponseChoices = {
  index: number;
  message: ChatResponseMessage;
  logprobs: number | null;
  finish_reason: string;
};

type ChatApiError = {
  message: string;
  type: string;
  param: string | null;
  code: string | null;
};

export type ChatResponse = {
  id: string;
  object: string;
  created: number;
  model: string;
  choices: ChatResponseChoices[];
  usage: {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
    prompt_tokens_details: {
      cached_tokens: number;
      audio_tokens: number;
    };
    completion_tokens_details: {
      reasoning_tokens: number;
      audio_tokens: number;
      accepted_prediction_tokens: number;
      rejected_prediction_tokens: number;
    };
  };
  service_tier: string;
  system_fingerprint: string;
  // If ai error occurs, field is populated
  error?: ChatApiError;
};

export type ChatApiResponse = {
  status: number; // http code
  data: ChatResponse;
};

export type OmnibarGPTQueryParams = {
  query: string | null;
  [key: string]: any;
};

export type OmnibarGPTQueryResult = {
  status: string;
  data: {
    function: string;
    args: {
      [key: string]: any;
    };
    [key: string]: any;
  };
  [key: string]: any;
};

// -----------------------------------------------
// ---------------- AI PROVIDER ------------------
// -----------------------------------------------

export const artificalIntelligenceProvider = {
  // Open AI chat - /core/openai/chat/
  openAIChat: async ({ messages, model }: ChatApiRequest): Promise<ChatApiResponse> => {
    try {
      const aiModel = model ?? DEFAULT_AI_MODEL;
      const payload: ChatApiRequest = {
        model: aiModel,
        messages: messages,
      };

      const path = `/core/openai/chat/`;

      const { data, status } = await httpClient<ChatResponse>({
        url: path,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: JSON.stringify(payload),
      });

      /**
       * endpoint differs slightly to others, formatting
       * response similar to other api results as it
       * does not return usual { status, data } format.
       * note: the request can succeed with 200, but if the
       * ai errors it will populate the data.error field
       */
      const result: ChatApiResponse = {
        status: status,
        data: data,
      };

      return result;
    } catch (error) {
      console.error('[openAIChat] Error', error);
      throw error;
    }
  },

  // Query the Omnibar AI API Endpoint
  getOmnibarGPTQuery: async ({
    query,
  }: OmnibarGPTQueryParams): Promise<OmnibarGPTQueryResult> => {
    try {
      const payload = {
        ...(query && { query: query }),
      };

      const path = `/core/openai/omnibar/?${fetchUtils.queryParameters(
        payload,
        stringifyOpts,
      )}`;

      const { data, status } = await httpClient({
        url: path,
        method: 'GET',
      });

      const result: OmnibarGPTQueryResult = {
        status: data?.status || status,
        data: {
          function: data?.data?.function,
          args: data?.data?.args,
        },
      };

      return result;
    } catch (error) {
      console.error('[getOmnibarGPTQuery] Error', error);
      throw error;
    }
  },
  // set as const - all keys (functions) in this object
  // are readonly and we should to ensure they
  // are immutable / cant be accidentally modified
} as const;

// Since all functions have their own signature with types
// we can safely use typeof helper to create the type for us
// and then '&' add it to the BloxCustomDataProvider type
export type ArtificialIntelligenceProviderType = typeof artificalIntelligenceProvider;
